/**
 * src/index.jsx
 *
 * Browser entrypoint for the main Hexatone SPA.
 *
 * It mounts App, installs development-time prop-type checking, owns version /
 * cache-busting startup behaviour, and registers the production service worker.
 */
import { render } from "preact";
import { options } from "preact";
import PropTypes from "prop-types";
import App from "./app.jsx";
import { debugLog, warnLog } from "./debug/logging.js";
import "./debug/sequencer-crash-diagnostics.js";
import "normalize.css";
import "./hex-style.css";
import "./loader.css";
import "./settings/settings.css";
import "./manual/manual-shared.css";
import "./keyboard/keyboard.css";

if (import.meta.env.DEV) {
  // installs global prop type checking for app preact components
  options.vnode = (vnode) => {
    let Component = vnode.type;
    if (Component && Component.propTypes) {
      PropTypes.checkPropTypes(Component.propTypes, vnode.props);
    }
  };
}

// ── Version tracking for cache busting ─────────────────────────────────────
// Derived from package.json via Vite so release versioning stays single-source.
const APP_VERSION = import.meta.env.VITE_APP_VERSION;

// Check stored version and force reload if mismatch
const storedVersion = localStorage.getItem("hexatone_version");
if (storedVersion && storedVersion !== APP_VERSION) {
  debugLog("lifecycle", `Version changed: ${storedVersion} → ${APP_VERSION}, clearing caches...`);

  // Clear all caches
  if ("caches" in window) {
    caches.keys().then((names) => {
      names.forEach((name) => caches.delete(name));
    });
  }

  // Unregister any service workers
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((reg) => reg.unregister());
    });
  }

  // Update stored version and reload
  localStorage.setItem("hexatone_version", APP_VERSION);
  window.location.reload();
} else {
  // Store current version
  localStorage.setItem("hexatone_version", APP_VERSION);
}

// ── Register service worker (production only) ──────────────────────────────
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    const serviceWorkerUrl = `${import.meta.env.BASE_URL}sw.js`;
    navigator.serviceWorker
      .register(serviceWorkerUrl)
      .then((reg) => {
        debugLog("lifecycle", "Service Worker registered:", reg.scope);

        // Check for updates on page load
        reg.update();

        // When a new SW is waiting, force it to activate
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
        }

        // When a new SW activates, reload the page
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              debugLog("lifecycle", "New version available, reloading...");
              newWorker.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch((err) => {
        warnLog("Service Worker registration failed:", err);
      });

    // Reload when controller changes (new SW took over)
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      window.location.reload();
    });
  });
}

render(<App />, document.getElementById("application"));
